import { useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { useCollection } from "@/hooks/useCollection";
import { crmPatch, errMsg } from "@/services/crmApi";
import { text, fmtTs, toDate } from "@/lib/format";
import { StatCard } from "@/components/shared/StatCard";

// Material lists customers send from the brugo.in marketplace ("Have a material list from
// your contractor?"): photos / PDFs and/or a typed list. Firestore customer_material_lists/{id}.
export const ML_STATUSES = [["new", "New"], ["in_review", "In review"], ["quoted", "Quoted"], ["ordered", "Ordered"], ["closed", "Closed"]];
const LABEL = Object.fromEntries(ML_STATUSES);
const TONE = { new: "bg-amber-50 text-amber-800 border-amber-200", in_review: "bg-sky-50 text-sky-700 border-sky-200", quoted: "bg-violet-50 text-violet-700 border-violet-200", ordered: "bg-emerald-50 text-emerald-700 border-emerald-200", closed: "bg-muted text-muted-foreground border-border" };
const Pill = ({ s }) => <span className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-medium ${TONE[s] || TONE.new}`} data-testid={`ml-status-${s}`}>{LABEL[s] || s}</span>;
const kb = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round((b || 0) / 1024))} KB`);
const isImg = (f) => String(f.content_type || "").startsWith("image/");

function ListDrawer({ m }) {
  const [st, setSt] = useState(m.status || "new");
  const [note, setNote] = useState(m.staff_note || "");
  const [quote, setQuote] = useState(m.quoted_amount ?? "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    const fields = {};
    if (st !== (m.status || "new")) fields.status = st;
    if (note !== (m.staff_note || "")) fields.staff_note = note;
    const q = quote === "" ? null : Number(quote);
    if (q !== (m.quoted_amount ?? null)) {
      if (q != null && !(q >= 0)) return toast.error("Quote must be a number");
      fields.quoted_amount = q;
    }
    if (!Object.keys(fields).length) return toast.info("Nothing changed");
    setBusy(true);
    try { await crmPatch("customer_material_lists", m.id, fields); toast.success("Material list updated"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <>
      <SheetHeader className="text-left">
        <SheetTitle className="font-heading">Material list {m.id}</SheetTitle>
        <SheetDescription>{text(m.customer_name)} · {m.customer_phone ? <a href={`tel:${m.customer_phone}`} className="underline">{m.customer_phone}</a> : "no phone"}{m.city ? ` · ${m.city}` : ""}</SheetDescription>
      </SheetHeader>
      <div className="mt-4 space-y-4">
        {m.text && (
          <div className="rounded-lg border border-border p-3" data-testid="ml-typed">
            <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-2">Typed list</p>
            <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed">{m.text}</pre>
            <Button size="sm" variant="ghost" className="mt-2 h-7 gap-1.5" onClick={() => { navigator.clipboard?.writeText(m.text); toast.success("Copied"); }}><Icons.Copy className="h-3.5 w-3.5" />Copy</Button>
          </div>
        )}
        {(m.files || []).length > 0 && (
          <div className="rounded-lg border border-border p-3" data-testid="ml-files">
            <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-2">Files ({m.files.length})</p>
            <div className="grid grid-cols-2 gap-2">
              {m.files.map((f, i) => (
                <a key={i} href={f.url} target="_blank" rel="noreferrer" className="group rounded-lg border border-border overflow-hidden hover:border-primary" data-testid={`ml-file-${i}`}>
                  {isImg(f)
                    ? <img src={f.url} alt={f.name} className="w-full h-32 object-cover" />
                    : <span className="flex h-32 items-center justify-center bg-muted"><Icons.FileText className="h-8 w-8 text-muted-foreground" /></span>}
                  <span className="block px-2 py-1.5 text-xs truncate">{f.name} <span className="text-muted-foreground">· {kb(f.size)}</span></span>
                </a>
              ))}
            </div>
          </div>
        )}
        {m.note && <div className="rounded-lg bg-muted/60 px-3 py-2 text-sm"><span className="font-medium">Customer note: </span>{m.note}</div>}
        <div className="rounded-lg border border-border p-3 space-y-3" data-testid="ml-edit">
          <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Follow-up</p>
          <div className="space-y-1.5"><Label>Status</Label>
            <Select value={st} onValueChange={setSt}><SelectTrigger data-testid="ml-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{ML_STATUSES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent></Select></div>
          <div className="space-y-1.5"><Label>Quoted amount (₹)</Label><Input type="number" min="0" value={quote} onChange={(e) => setQuote(e.target.value)} placeholder="Optional" data-testid="ml-quote" /></div>
          <div className="space-y-1.5"><Label>Internal note</Label><Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Called the customer, sourcing from…" data-testid="ml-note" /></div>
          <Button onClick={save} disabled={busy} className="w-full" data-testid="ml-save">{busy ? "Saving…" : "Save"}</Button>
        </div>
        {(m.status_history || []).length > 0 && (
          <div className="text-xs text-muted-foreground space-y-1">
            {[...m.status_history].reverse().map((h, i) => <p key={i}>{LABEL[h.status] || h.status} · {fmtTs(h.at)} · {text(h.by, "")}</p>)}
          </div>
        )}
        <p className="text-[11px] text-muted-foreground">Received {fmtTs(m.created_at || m.created_at_iso)} from the website marketplace.</p>
      </div>
    </>
  );
}

export function MaterialLists() {
  const rows = useCollection("customer_material_lists");
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("open");
  const [open, setOpen] = useState(null);
  const sorted = useMemo(() => [...rows].sort((a, b) => (toDate(b.created_at || b.created_at_iso)?.getTime() || 0) - (toDate(a.created_at || a.created_at_iso)?.getTime() || 0)), [rows]);
  const shown = sorted.filter((m) => (filter === "all" || (filter === "open" ? !["ordered", "closed"].includes(m.status) : m.status === filter))
    && (!q || JSON.stringify(m).toLowerCase().includes(q.toLowerCase())));
  const sel = rows.find((m) => m.id === open);
  const count = (s) => rows.filter((m) => (m.status || "new") === s).length;
  return (
    <div data-testid="material-lists">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard testId="ml-kpi-new" index={0} label="New lists" value={String(count("new"))} icon="Inbox" accent="amber" critical={count("new") > 0} />
        <StatCard testId="ml-kpi-review" index={1} label="In review" value={String(count("in_review"))} icon="Search" accent="cyan" />
        <StatCard testId="ml-kpi-quoted" index={2} label="Quoted" value={String(count("quoted"))} icon="ReceiptText" accent="primary" />
        <StatCard testId="ml-kpi-ordered" index={3} label="Ordered" value={String(count("ordered"))} icon="CheckCircle2" accent="emerald" />
      </div>
      <Card>
        <div className="p-4 border-b border-border flex flex-wrap items-center gap-3">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, phone, item…" className="max-w-sm" data-testid="ml-search" />
          <Select value={filter} onValueChange={setFilter}><SelectTrigger className="w-44" data-testid="ml-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="open">Open</SelectItem><SelectItem value="all">All</SelectItem>{ML_STATUSES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent></Select>
        </div>
        <div className="overflow-x-auto"><Table>
          <TableHeader><TableRow><TableHead>List</TableHead><TableHead>Customer</TableHead><TableHead>What they sent</TableHead><TableHead>City</TableHead><TableHead>Status</TableHead><TableHead>Received</TableHead></TableRow></TableHeader>
          <TableBody>
            {shown.map((m) => (
              <TableRow key={m.id} className="cursor-pointer" onClick={() => setOpen(m.id)} data-testid={`ml-row-${m.id}`}>
                <TableCell className="font-mono text-xs">{m.id}</TableCell>
                <TableCell className="text-sm">{text(m.customer_name)}<span className="block text-[10px] text-muted-foreground">{text(m.customer_phone, "")}</span></TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  <span className="flex items-center gap-2">
                    {(m.files || []).length > 0 && <span className="inline-flex items-center gap-1"><Icons.Paperclip className="h-3.5 w-3.5" />{m.files.length} file{m.files.length > 1 ? "s" : ""}</span>}
                    {m.text && <span className="inline-flex items-center gap-1 truncate max-w-[220px]"><Icons.Type className="h-3.5 w-3.5 shrink-0" />{m.text.split("\n")[0]}</span>}
                  </span>
                </TableCell>
                <TableCell className="text-sm">{text(m.city)}</TableCell>
                <TableCell><Pill s={m.status || "new"} /></TableCell>
                <TableCell className="text-xs text-muted-foreground">{fmtTs(m.created_at || m.created_at_iso)}</TableCell>
              </TableRow>
            ))}
            {shown.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-10 text-muted-foreground">No material lists{filter === "open" ? " waiting" : ""}.</TableCell></TableRow>}
          </TableBody>
        </Table></div>
      </Card>
      <Sheet open={!!sel} onOpenChange={(v) => !v && setOpen(null)}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="ml-drawer">{sel && <ListDrawer key={sel.id} m={sel} />}</SheetContent>
      </Sheet>
    </div>
  );
}
